/**
 * @fileoverview Settings page — tabbed platform configuration UI.
 * @module system-web/pages/SettingsPage
 *
 * Responsibilities:
 * - Render the Settings page header ("Settings" / "Platform configuration").
 * - Provide a tabbed layout using the existing TabPanel component.
 * - Lazy-load the AI Models, AI Task Assignments, API Usage, Clusters, and Parcel Data sections.
 *
 * Dependencies:
 * - React (lazy, Suspense)
 * - ../components/ui/TabPanel
 * - ./settings/AiModelsSection
 * - ./settings/AiTaskAssignmentsSection
 * - ./settings/ApiUsageSection
 * - ./settings/ClustersSection
 * - ./settings/ParcelDataSection
 *
 * Architecture:
 * - Page as composition only — orchestrates layout and route context.
 * - Each tab section is a self-contained component that reads from Dexie-backed hooks.
 * - No data synchronization, direct fetch logic, or domain workflows in this page.
 *
 * Notes:
 * - Sections are lazy-loaded to keep the initial Settings bundle small.
 * - No direct apiClient calls from this page or its sections.
 *
 * @component
 * Purpose:     Top-level Settings page with tabbed navigation.
 * Props:       (none — route component).
 * Behavior:    Renders TabPanel with tabs: AI Models, AI Task Assignments, API Usage,
 *              Clusters, and Parcel Data.
 * Data Source: local (each section reads from Dexie via its own hook).
 * Constraints: no direct API calls; no fetch in useEffect.
 * Performance: Sections are lazy-loaded with Suspense fallback.
 */

import React, { lazy, Suspense } from 'react';
import { Settings as SettingsIcon, Loader2 } from 'lucide-react';
import TabPanel from '../components/ui/TabPanel';

const AiModelsSection = lazy(() => import('./settings/AiModelsSection'));
const AiTaskAssignmentsSection = lazy(() => import('./settings/AiTaskAssignmentsSection'));
const ApiUsageSection = lazy(() => import('./settings/ApiUsageSection'));
const ClustersSection = lazy(() => import('./settings/ClustersSection'));
const ParcelDataSection = lazy(() => import('./settings/ParcelDataSection'));

function SectionFallback() {
  return (
    <div className="flex items-center justify-center h-full py-16">
      <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="theme-grid flex flex-col h-full">
      {/* Page header */}
      <div className="shrink-0 px-6 pt-4 pb-2">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Platform configuration</p>
      </div>

      {/* Tabbed content */}
      <div className="flex-1 min-h-0">
        <TabPanel
          tabs={[
            {
              id: 'ai-models',
              label: 'AI Models',
              content: (
                <Suspense fallback={<SectionFallback />}>
                  <AiModelsSection />
                </Suspense>
              ),
            },
            {
              id: 'ai-task-assignments',
              label: 'AI Task Assignments',
              content: (
                <Suspense fallback={<SectionFallback />}>
                  <AiTaskAssignmentsSection />
                </Suspense>
              ),
            },
            {
              id: 'api-usage',
              label: 'API Usage',
              content: (
                <Suspense fallback={<SectionFallback />}>
                  <ApiUsageSection />
                </Suspense>
              ),
            },
            {
              id: 'clusters',
              label: 'Clusters',
              content: (
                <Suspense fallback={<SectionFallback />}>
                  <ClustersSection />
                </Suspense>
              ),
            },
            {
              id: 'parcel-data',
              label: 'Parcel Data',
              content: (
                <Suspense fallback={<SectionFallback />}>
                  <ParcelDataSection />
                </Suspense>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
